// Cost display helpers — user-facing values are shown at 150% of the
// actual computed cost. Backend billing math is untouched; this
// multiplier lives ONLY at the presentation layer.
//
// Rules:
// - displayCost:      whole units (credits / tokens / generations)
// - displayCurrency:  currency values, preserves the caller's decimal
//                      places so the "$0.0016 per clip" precision doesn't
//                      collapse to "$0.00".
// - fromDisplayCurrency / fromDisplayCost: inverse helpers used when a
//   user edits a dollar-input in the UI and we need to persist the
//   underlying (backend) value.
//
// Every "credits / cost / est. / spend / budget" render in the app
// should go through one of these helpers so the 1.5x scaling is
// consistent and audit-able.

export const COST_DISPLAY_MULTIPLIER = 1.5;

export const displayCost = (n) => Math.round((n ?? 0) * COST_DISPLAY_MULTIPLIER);

// Precision rule: at 2-decimal (default) presentation the ×1.5 ratio
// can drift on sub-cent amounts. E.g. raw $0.0011 × 1.5 = $0.00165,
// which would collapse to $0.0016 at 4-decimal round-half-even —
// showing a ratio of 1.4545 to a scrutinising auditor. We auto-bump
// precision when the multiplied value is < 0.01 so the ×1.5 contract
// holds at display too.
export const displayCurrency = (n, digits = 2) => {
    const raw = Number.isFinite(n) ? n : 0;
    const val = raw * COST_DISPLAY_MULTIPLIER;
    let d = digits;
    // Bump precision to 7 decimals when EITHER the raw or the
    // multiplied value falls under 1 cent so the ×1.5 ratio holds at
    // display precision. E.g. raw $0.001065 × 1.5 = $0.0015975. At 4
    // decimals it collapses to $0.0016 (ratio 1.502); at 5 or 6 it
    // still lands off due to round-half-even on binary floats; at 7
    // it stays $0.0015975 → ratio 1.5000 exactly.
    if ((raw > 0 && Math.abs(raw) < 0.01) || (val > 0 && Math.abs(val) < 0.01)) {
        d = Math.max(d, 7);
    }
    return val.toFixed(d);
};

// THE helper for every customer money figure: raw × 1.5, always 2 decimals.
export const displayMoney = (n) => (
    ((Number.isFinite(Number(n)) ? Number(n) : 0) * COST_DISPLAY_MULTIPLIER).toFixed(2)
);

// ONE length formatter for every "x.x min" figure (slider, badges, chips).
export const fmtMinutes = (seconds) => ((Number(seconds) || 0) / 60).toFixed(1);
export const lengthOutOfRange = (seconds) => {
    const m = (Number(seconds) || 0) / 60;
    return m > 0 && (m < 5 || m > 15);
};

// Numeric variant for `<Input type="number">` value binding.
export const displayCurrencyNumber = (n, digits = 2) => (
    Number(displayCurrency(n, digits))
);

// Inverse — convert a customer-facing (1.5x) figure back to the raw
// backend value. Used on save handlers so admin edits round-trip cleanly.
export const fromDisplayCurrency = (n) => (
    (Number.isFinite(Number(n)) ? Number(n) : 0) / COST_DISPLAY_MULTIPLIER
);

export const fromDisplayCost = (n) => (
    Math.round((Number.isFinite(Number(n)) ? Number(n) : 0) / COST_DISPLAY_MULTIPLIER)
);
